import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import axiosInstance from '../components/axiosInstance';

const BookRecommender = () => {
  const [mood, setMood] = useState('');
  const [recommendations, setRecommendations] = useState([]);
  const [allBooks, setAllBooks] = useState([]);
 const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSideNavOpen, setIsSideNavOpen] = useState(false);

  const toggleSideNav = () => setIsSideNavOpen(!isSideNavOpen);

  // Fetch books from database on component mount
  useEffect(() => {
    const fetchBooks = async () => {
      setIsLoading(true);
      try {
        const response = await axiosInstance.get('/activity/books/');
        // Map API field names to match component expectations
        const booksData = response.data.map(book => ({
          ...book,
          mood: book.moods ? book.moods.split(',').map(m => m.trim()) : [], // Parse comma-separated moods to array
          coverImage: book.cover_image_url || 'https://via.placeholder.com/300x450?text=No+Cover',
          link: book.purchase_url,
        }));
        setAllBooks(booksData);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching books:', err);
        setError('Failed to load books. Please try again later.');
        setIsLoading(false);
      }
    };

    fetchBooks();
  }, []);

  const getRecommendations = () => {
    const lowerMood = mood.toLowerCase().trim();
    const recommendedBooks = allBooks.filter(book => 
      book.mood.some(bookMood => bookMood.toLowerCase().includes(lowerMood))
    );
  
    if (recommendedBooks.length === 0) {
      setRecommendations([{
        isEmpty: true // Add a flag for empty result
      }]);
    } else {
      setRecommendations(recommendedBooks);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (mood.trim()) {
      getRecommendations();
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Sidebar */}
      <div className={`transform ${isSideNavOpen ? 'translate-x-0' : '-translate-x-full'} w-[280px] bg-white shadow-lg transition-transform duration-300 ease-in-out z-20 md:translate-x-0 smallscreensidenav`}>
        <div className="p-6">
          <nav>
            <ul className="space-y-2">
              <li><button className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded"><Link to="/sereniai" className="flex items-center font-semibold">🤖 SereniAI</Link></button></li>
              <li><button className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded"><Link to="/book-professionals" className="flex items-center font-semibold">📚 Book our Professionals</Link></button></li>
              <li><button className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded"><Link to="/activity-lounge" className="flex items-center font-semibold">🎮 Activity Lounge</Link></button></li>
              <li><button className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded"><Link to="/blog" className="flex items-center font-semibold">📝 Blog</Link></button></li>
              <li><button className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded"><Link to="/journal" className="flex items-center font-semibold">✍🏼 Your Journal</Link></button></li>
              <li><button className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded"><Link to="/community" className="flex items-center font-semibold">👥 Community</Link></button></li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 p-6 overflow-hidden container mx-auto px-4 w-[900px] overflow-y-auto md:h-[560px]">
        <div className="max-w-4xl mx-auto">
          <header className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold">Mood-Based Book Recommender</h1>
          </header>

          <form onSubmit={handleSearch} className="mb-8">
            <div className="flex gap-2">
              <input
                type="text"
                value={mood}
                onChange={(e) => setMood(e.target.value)}
                placeholder="How are you feeling?"
                className="flex-1 md:px-4 md:py-2 px-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#59844F]"
              />
              <button type="submit" className="px-4 py-2 bg-[#59844F] text-white rounded-md hover:bg-[#59844F] focus:outline-none focus:ring-2 focus:ring-[#59844F] focus:ring-offset-2">
                <FontAwesomeIcon icon={faSearch} className="mr-2" />
                Get Recommendations
              </button>
            </div>
          </form>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-1">
            {error ? (
              <div className="col-span-full text-center py-8">
                <p className="text-xl text-red-600">{error}</p>
              </div>
            ) : isLoading && !allBooks.length ? (
              <p className="text-center col-span-full">Loading books...</p>
            ) : recommendations.length > 0 ? (
              recommendations.map((book, index) => (
                !book.isEmpty ? (
                  <div key={index} className="bg-white p-6 rounded-lg shadow-md md:flex">
                    <img src={book.coverImage} alt={`Cover of ${book.title}`} className="md:w-32 md:h-48 rounded-md mr-6 flex justify-center" />
                    <div>
                      <h3 className="md:text-xl font-semibold my-2 text-base">{book.title}</h3>
                      {book.author && <p className="text-gray-600 mb-2">{book.author}</p>}
                      <p className="text-sm text-gray-500 mb-3">Mood: {book.mood.join(', ')}</p>
                      <p className="text-gray-700 mb-3">{book.description}</p>
                      {book.link && (
                        <div className="mt-4">
                          <a href={book.link} target="_blank" rel="noopener noreferrer" className="text-[#59844F] hover:underline">
                            <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-2" />
                            Get This Book
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div key={index} className='flex justify-center text-center col-span-full'>
                    <div className="text-center py-8">
                      <p className="text-xl text-gray-600 mb-4">We couldn't find books for that mood.</p>
                      <p className="text-lg text-gray-500">
                        Help us improve! <Link to="/contact" className="text-[#59844F] hover:underline">Contact us</Link> to suggest books for this mood.
                      </p>
                    </div>
                  </div>
                )
              ))
            ) : null}
          </div>
        </div>
      </main>
    </div>
  );
};

export default BookRecommender;
